import {
  createAsyncThunk,
  createSelector,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';

import type { RootState } from 'features/app/store/store';
import type { TerminationType } from 'features/call/call-base/enums';
import type { Call } from 'features/call/call-base/interfaces';
import {
  callIdSelector,
  selectActualCallOwnerUserId,
  setCallId,
  setRegistrationType,
  setRelayCallState,
} from 'features/call/call-base/store';
import { resetDeafConnectionHistory } from 'features/call/call-deaf/store';
import { resetPrimaryHearingName } from 'features/call/call-hearing/store';
import { handleError } from 'features/notification/store';
import { rnsConnectionIdSelector } from 'features/session/store';
import type { PendingSpawnedCallData } from 'features/spawn-call/interfaces';
import { SpawnCallDaoService } from 'features/spawn-call/services';
import {
  isRequestingUserSelector,
  isTeamingInProgressSelector,
  isTeamingStartedSelector,
} from 'features/teaming/teaming-base/store';
import { agentNumberSelector } from 'features/user/store';
import { isPrimaryVcoSenderSelector } from 'features/vco/store';

const RESET_CALL_ACTION_TYPE = 'call/resetCall';

interface StageSpawnedCallDataPayload {
  call: Call;
  calleeNumber?: string;
}

export interface SpawnCallState {
  enabled: boolean;
  callerCoreUserId: number | null;
  pendingSpawnedCallData: PendingSpawnedCallData | null;
}

export const initialSpawnCallState: SpawnCallState = {
  enabled: false,
  callerCoreUserId: null,
  pendingSpawnedCallData: null,
};

export const spawnCallSlice = createSlice({
  name: 'spawnCall',
  initialState: initialSpawnCallState,
  reducers: {
    enableSpawnCall: (state) => {
      state.enabled = true;
    },
    disableSpawnCall: (state) => {
      state.enabled = false;
    },
    pendingSpawnedCallDataReceived: (
      state,
      action: PayloadAction<PendingSpawnedCallData>
    ) => {
      state.callerCoreUserId = action.payload.coreUserId;
      state.pendingSpawnedCallData = action.payload;
    },
    spawnedCallCalleeNumberResolved: (
      state,
      action: PayloadAction<Pick<Call, 'CalleeConnectionString'>>
    ) => {
      if (!state.pendingSpawnedCallData) {
        return;
      }

      state.pendingSpawnedCallData.calleeNumber =
        action.payload.CalleeConnectionString;
    },
    clearPendingSpawnedCallData: (state) => {
      state.pendingSpawnedCallData = null;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type === RESET_CALL_ACTION_TYPE,
      (state) => {
        state.callerCoreUserId = null;
        state.pendingSpawnedCallData = null;
      }
    );
  },
});

export const {
  enableSpawnCall,
  disableSpawnCall,
  pendingSpawnedCallDataReceived,
  spawnedCallCalleeNumberResolved,
  clearPendingSpawnedCallData,
} = spawnCallSlice.actions;

export const spawnCallReducer = spawnCallSlice.reducer;

export const isSpawnCallEnabledSelector = ({ spawnCall }: RootState) =>
  spawnCall.enabled;

export const pendingSpawnedCallDataSelector = ({ spawnCall }: RootState) =>
  spawnCall.pendingSpawnedCallData;

export const stageSpawnedCallData = createAsyncThunk(
  'spawnCall/stageSpawnedCallData',
  async (
    { call, calleeNumber }: StageSpawnedCallDataPayload,
    { dispatch, getState }
  ) => {
    const state = getState() as RootState;
    const agentNumber = agentNumberSelector(state);
    const coreUserId =
      call.CoreServicesUserId ?? state.spawnCall.callerCoreUserId;
    const isPrimaryVcoSender = isPrimaryVcoSenderSelector(state);

    if (
      !isPrimaryVcoSender ||
      !call.IsDeafToHearing ||
      coreUserId == null ||
      !agentNumber
    ) {
      return;
    }

    dispatch(
      pendingSpawnedCallDataReceived({
        coreUserId,
        calleeNumber: calleeNumber ?? call.CalleeConnectionString,
        callId: call.Id,
        agentNumber,
      })
    );
  }
);

export const sendPendingSpawnedCallData = createAsyncThunk(
  'spawnCall/sendPendingSpawnedCallData',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState;
    const pendingSpawnedCallData = pendingSpawnedCallDataSelector(state);
    const currentCallId = callIdSelector(state);
    const isPrimaryVcoSender = isPrimaryVcoSenderSelector(state);

    if (!pendingSpawnedCallData) {
      return;
    }

    if (!pendingSpawnedCallData.calleeNumber) {
      return;
    }

    dispatch(clearPendingSpawnedCallData());

    if (
      pendingSpawnedCallData.callId !== currentCallId ||
      !isPrimaryVcoSender
    ) {
      return;
    }

    try {
      await SpawnCallDaoService.sendSpawnedCallData(pendingSpawnedCallData);
    } catch (error) {
      dispatch(
        handleError({
          error,
          methodName: 'sendPendingSpawnedCallData',
          disableUserNotification: true,
        })
      );
    }
  }
);

export const shouldCallControlsBeVisibleSelector = createSelector(
  isTeamingInProgressSelector,
  isTeamingStartedSelector,
  isRequestingUserSelector,
  (isTeamingInProgress, isTeamingStarted, isRequestingUser) =>
    !isTeamingInProgress || (isTeamingStarted && isRequestingUser)
);

export const spawnCall = createAsyncThunk(
  'spawnCall/spawn',
  async (
    terminationReason: TerminationType,
    { dispatch, getState, rejectWithValue }
  ) => {
    const state = getState() as RootState;
    const callId = callIdSelector(state);
    const sessionId = rnsConnectionIdSelector(state);
    const actualCallOwnerUserId = selectActualCallOwnerUserId(state);

    try {
      const call = await SpawnCallDaoService.spawnCall({
        callId,
        sessionId,
        terminationReason,
        userId: actualCallOwnerUserId,
      });

      dispatch(resetDeafConnectionHistory());
      dispatch(setCallId(call.Id));
      dispatch(setRelayCallState(call.CallState));
      dispatch(setRegistrationType(call.RegistrationType));
      if (call.IsDeafToHearing) {
        dispatch(resetPrimaryHearingName());
      }
      await dispatch(stageSpawnedCallData({ call })).unwrap();
      return call;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
